import axios from 'axios';
import { differenceInDays, format } from 'date-fns';
import {
    bg,
    cs,
    de,
    el,
    enUS,
    es,
    fr,
    it,
    ms,
    nl,
    nb,
    pl,
    ro,
    ru,
    sk,
    sq,
    srLatn,
    sv,
    zhCN,
} from 'date-fns/locale';
import { get } from 'lodash';

import {
    apiConfig,
    COOKIE_NAME,
    WIDGET_LOCAL_STORAGE_KEY,
    WIDGET_STATS_SETTINGS,
} from '../config';

export const getWidgetPosition = ({
    position = 'bottom-right',
    vertical: verticalOffset = '30px',
    horizontal: horizontalOffset = '30px',
} = {}) => {
    const [vertical, horizontal] =
        position?.split('-') || 'bottom-right'.split('-');

    return {
        [vertical]: verticalOffset || '30px',
        [horizontal]: horizontalOffset || '30px',
    };
};

export const getOpenWidgetBtnPosition = (widgetPosition = 'bottom-right') => {
    // eslint-disable-next-line no-unused-vars
    const [vertical, horizontal] =
        widgetPosition?.split('-') || 'bottom-right'.split('-');

    return horizontal === 'left'
        ? { marginRight: 'auto' }
        : { marginLeft: 'auto' };
};

const languageCodeMap = {
    cs: 'cs-CZ',
    de: 'de-DE',
    el: 'el-GR',
    fr: 'fr-FR',
    it: 'it-IT',
    ms: 'ms-MY',
    ru: 'ru-RU',
    sk: 'sk-SK',
    'en-US': 'en-US',
};

export const getLanguageCode = () => {
    const lang = document.querySelector('html').getAttribute('lang');
    const langCode = languageCodeMap[lang];

    if (Object.values(languageCodeMap).indexOf(lang) !== -1) {
        return lang;
    }

    return langCode || '';
};

export const getCookie = (name) => {
    try {
        const entry = document.cookie
            .split(';')
            .find((str) => str.trim().startsWith(name));

        if (entry) {
            return entry.split('=').pop();
        }
        return null;
    } catch (error) {
        throw new Error(error);
    }
};

export const isMobileDeviceRequest = () => {
    try {
        let check = false;
        (function (a) {
            if (
                // eslint-disable-next-line no-useless-escape
                /(android|bb\d+|meego).+mobile|avantgo|bada\/|blackberry|blazer|compal|elaine|fennec|hiptop|iemobile|ip(hone|od)|iris|kindle|lge |maemo|midp|mmp|mobile.+firefox|netfront|opera m(ob|in)i|palm( os)?|phone|p(ixi|re)\/|plucker|pocket|psp|series(4|6)0|symbian|treo|up\.(browser|link)|vodafone|wap|windows ce|xda|xiino|android|ipad|playbook|silk/i.test(
                    a
                ) ||
                // eslint-disable-next-line no-useless-escape
                /1207|6310|6590|3gso|4thp|50[1-6]i|770s|802s|a wa|abac|ac(er|oo|s\-)|ai(ko|rn)|al(av|ca|co)|amoi|an(ex|ny|yw)|aptu|ar(ch|go)|as(te|us)|attw|au(di|\-m|r |s )|avan|be(ck|ll|nq)|bi(lb|rd)|bl(ac|az)|br(e|v)w|bumb|bw\-(n|u)|c55\/|capi|ccwa|cdm\-|cell|chtm|cldc|cmd\-|co(mp|nd)|craw|da(it|ll|ng)|dbte|dc\-s|devi|dica|dmob|do(c|p)o|ds(12|\-d)|el(49|ai)|em(l2|ul)|er(ic|k0)|esl8|ez([4-7]0|os|wa|ze)|fetc|fly(\-|_)|g1 u|g560|gene|gf\-5|g\-mo|go(\.w|od)|gr(ad|un)|haie|hcit|hd\-(m|p|t)|hei\-|hi(pt|ta)|hp( i|ip)|hs\-c|ht(c(\-| |_|a|g|p|s|t)|tp)|hu(aw|tc)|i\-(20|go|ma)|i230|iac( |\-|\/)|ibro|idea|ig01|ikom|im1k|inno|ipaq|iris|ja(t|v)a|jbro|jemu|jigs|kddi|keji|kgt( |\/)|klon|kpt |kwc\-|kyo(c|k)|le(no|xi)|lg( g|\/(k|l|u)|50|54|\-[a-w])|libw|lynx|m1\-w|m3ga|m50\/|ma(te|ui|xo)|mc(01|21|ca)|m\-cr|me(rc|ri)|mi(o8|oa|ts)|mmef|mo(01|02|bi|de|do|t(\-| |o|v)|zz)|mt(50|p1|v )|mwbp|mywa|n10[0-2]|n20[2-3]|n30(0|2)|n50(0|2|5)|n7(0(0|1)|10)|ne((c|m)\-|on|tf|wf|wg|wt)|nok(6|i)|nzph|o2im|op(ti|wv)|oran|owg1|p800|pan(a|d|t)|pdxg|pg(13|\-([1-8]|c))|phil|pire|pl(ay|uc)|pn\-2|po(ck|rt|se)|prox|psio|pt\-g|qa\-a|qc(07|12|21|32|60|\-[2-7]|i\-)|qtek|r380|r600|raks|rim9|ro(ve|zo)|s55\/|sa(ge|ma|mm|ms|ny|va)|sc(01|h\-|oo|p\-)|sdk\/|se(c(\-|0|1)|47|mc|nd|ri)|sgh\-|shar|sie(\-|m)|sk\-0|sl(45|id)|sm(al|ar|b3|it|t5)|so(ft|ny)|sp(01|h\-|v\-|v )|sy(01|mb)|t2(18|50)|t6(00|10|18)|ta(gt|lk)|tcl\-|tdg\-|tel(i|m)|tim\-|t\-mo|to(pl|sh)|ts(70|m\-|m3|m5)|tx\-9|up(\.b|g1|si)|utst|v400|v750|veri|vi(rg|te)|vk(40|5[0-3]|\-v)|vm40|voda|vulc|vx(52|53|60|61|70|80|81|83|85|98)|w3c(\-| )|webc|whit|wi(g |nc|nw)|wmlb|wonu|x700|yas\-|your|zeto|zte\-/i.test(
                    a.substr(0, 4)
                )
            )
                check = true;
        })(navigator.userAgent || navigator.vendor || window.opera);
        return check;
    } catch (err) {
        throw new Error(err);
    }
};

export const makeStatsRequest = async (action, propertyId, data) => {
    const langCode = getLanguageCode();
    const cookie = getCookie(COOKIE_NAME);
    const mobileRequest = isMobileDeviceRequest();

    try {
        const response = await axios.get(apiConfig.baseStatsUrl, {
            params: {
                propertyID: propertyId,
                lang: langCode,
                cookie: cookie,
                widgetAction: action,
                mobile: mobileRequest,
                ...data,
            },
        });

        if (response.status === 200) {
            const miniBookingWidgetSaveData = getLocalStorageData(
                WIDGET_LOCAL_STORAGE_KEY
            );

            if (action === WIDGET_STATS_SETTINGS.actions.open) {
                miniBookingWidgetSaveData.lastOpenRequestTimestamp = Date.now();
            } else if (
                action === WIDGET_STATS_SETTINGS.actions.click ||
                action === WIDGET_STATS_SETTINGS.actions.offersClick
            ) {
                miniBookingWidgetSaveData.lastClickRequestTimestamp =
                    Date.now();
            }

            localStorage.setItem(
                WIDGET_LOCAL_STORAGE_KEY,
                JSON.stringify(miniBookingWidgetSaveData)
            );
        }
    } catch (error) {}
};

export const makeStatsRequestThrottled = (action, propertyId) => {
    const { lastOpenRequestTimestamp, lastClickRequestTimestamp } =
        getLocalStorageData(WIDGET_LOCAL_STORAGE_KEY);
    const now = Date.now();

    switch (action) {
        case WIDGET_STATS_SETTINGS.actions.open:
            if (
                lastOpenRequestTimestamp === undefined ||
                now - lastOpenRequestTimestamp >=
                    WIDGET_STATS_SETTINGS.intervals.openInterval
            ) {
                makeStatsRequest(action, propertyId);
            }
            break;
        case WIDGET_STATS_SETTINGS.actions.click:
        case WIDGET_STATS_SETTINGS.actions.offersClick:
            if (
                lastClickRequestTimestamp === undefined ||
                now - lastClickRequestTimestamp >=
                    WIDGET_STATS_SETTINGS.intervals.clickInterval
            ) {
                makeStatsRequest(action, propertyId);
            }
            break;
        case WIDGET_STATS_SETTINGS.actions.booknow:
            makeStatsRequest(action, propertyId);
            break;
        case WIDGET_STATS_SETTINGS.actions.phone:
            makeStatsRequest(action, propertyId);
            break;
    }
};

export const getLocalStorageData = (key) => {
    const data = localStorage.getItem(key);

    return data ? JSON.parse(data) : {};
};

export const addToCartLocalStorageData = (key, value) => {
    const existingData = localStorage.getItem(key);
    const dataArray = existingData ? JSON.parse(existingData) : [];
    const isRateIDExists = dataArray.some(
        (item) => item.rateID === value.rateID
    );
    if (!isRateIDExists) {
        dataArray.push(value);
        localStorage.setItem(key, JSON.stringify(dataArray));
    }
};

export const removeFromCartLocalStorageData = (key) => {
    const existingData = localStorage.getItem('cart');
    const dataArray = existingData ? JSON.parse(existingData) : [];
    const updatedCartData = dataArray.filter(
        (cartItem) => cartItem.rateID !== key
    );
    localStorage.setItem('cart', JSON.stringify(updatedCartData));
};

export const getsessionStorageData = (key, defaultValue = {}) => {
    const data = sessionStorage.getItem(key);

    return data ? JSON.parse(data) : defaultValue;
};

export const areRatesReady = (ourPrice) => {
    if (
        (typeof ourPrice.price === 'string' &&
            ourPrice.price.startsWith('getprice')) ||
        (typeof ourPrice.loyaltyprice === 'string' &&
            ourPrice.loyaltyprice.startsWith('getprice'))
    ) {
        return false;
    }

    return true;
};

export const getPricesUrl = (queryString) => {
    let url;

    if (process.env.NODE_ENV === 'production') {
        url = apiConfig.baseUrl + '?' + queryString;
    } else if (process.env.REACT_APP_DEV_ENV === 'local') {
        url = apiConfig.localUrl;
    } else if (process.env.REACT_APP_DEV_ENV === 'demo') {
        url = apiConfig.demoUrl + '?' + queryString;
    } else {
        url = apiConfig.developementUrl + queryString;
    }

    return url;
};

export const isZeroNumber = (number) => {
    const zeroNumberRe = /^[0\\.,]+$/;

    let _isZeroNumber = false;

    switch (typeof number) {
        case 'number':
            _isZeroNumber = number === 0;
            break;
        case 'string':
            _isZeroNumber = zeroNumberRe.test(number.trim());
            break;
        default:
            _isZeroNumber = false;
    }
    if (typeof number === 'number') {
        return number === 0;
    }

    return _isZeroNumber;
};

export const isNumber = (number) => {
    // eslint-disable-next-line no-useless-escape
    const isNumberRe = /^\d*([\d\.?,?\d])*\d*$/;

    let _isNumber = false;

    switch (typeof number) {
        case 'number':
            _isNumber = true;
            break;
        case 'string':
            _isNumber = number !== '' && isNumberRe.test(number.trim());
            break;
        default:
            _isNumber = false;
    }

    return _isNumber;
};

export const isValidPrice = (price) => {
    let isValid = false;

    switch (typeof price) {
        case 'string':
            isValid = isNumber(price.trim()) && !isZeroNumber(price.trim());
            break;
        case 'number':
            isValid = price > 0;
            break;
        default:
            isValid = false;
    }

    return isValid;
};

export const saveWidgetStateToLS = (state = {}) => {
    const miniBookingWidgetSaveData = getLocalStorageData(
        WIDGET_LOCAL_STORAGE_KEY
    );
    for (const key in state) {
        const value = state[key];
        miniBookingWidgetSaveData[key] = value;
    }
    localStorage.setItem(
        WIDGET_LOCAL_STORAGE_KEY,
        JSON.stringify(miniBookingWidgetSaveData)
    );
};

export const checkWidgetIntervalsToDelete = (intervalOpts = {}) => {
    const miniBookingWidgetSaveData = getLocalStorageData(
        WIDGET_LOCAL_STORAGE_KEY
    );
    const now = Date.now();
    if (
        miniBookingWidgetSaveData[intervalOpts.intervalTimestampKey] ===
            undefined ||
        now - miniBookingWidgetSaveData[intervalOpts.intervalTimestampKey] >=
            WIDGET_STATS_SETTINGS.intervals.openMultipropertyInterval
    ) {
        delete miniBookingWidgetSaveData[intervalOpts.intervalKey];
        delete miniBookingWidgetSaveData[intervalOpts.intervalTimestampKey];
    }

    localStorage.setItem(
        WIDGET_LOCAL_STORAGE_KEY,
        JSON.stringify(miniBookingWidgetSaveData)
    );
};

export const applyImportantStyles = (el, stylesObj) => {
    if (el) {
        for (const prop in stylesObj) {
            el.style.setProperty(prop, stylesObj[prop], 'important');
        }
    }
};

export const addHashToHexColor = (hexColor = '') => {
    if (hexColor.startsWith('#')) {
        return hexColor;
    }

    return '#' + hexColor;
};

export const stripHTMLTags = (html = '') => {
    if (typeof html !== 'string') {
        return '';
    }

    return html.replace(/(<([^>]+)>)/gi, '');
};

export const formatDate = (date, formatStr, countryCode) => {
    const locales = {
        bg,
        cs,
        de,
        el,
        enUS,
        es,
        fr,
        it,
        ms,
        nl,
        nb,
        pl,
        ro,
        ru,
        sk,
        sq,
        srLatn,
        sv,
        zhCN,
    };
    let localeCode;

    switch (countryCode) {
        case 'en-US':
            localeCode = 'enUS';
            break;
        case 'sr-Latn-RS':
            localeCode = 'srLatn';
            break;
        case 'zh-CN':
            localeCode = 'zhCN';
            break;
        default:
            localeCode = countryCode.split('-')[0];
    }

    return format(date, formatStr, {
        locale: locales[localeCode],
    });
};

/**
 * Get the number of full day periods between two dates
 *
 * @param {string} dateLeft - the later date
 * @param {string} dateRight - the earlier date
 *
 */
export const getDifferenceInDays = (dateLeft, dateRight) => {
    const dateLeftObj = new Date(dateLeft);
    const dateRightObj = new Date(dateRight);

    return differenceInDays(dateLeftObj, dateRightObj);
};

export const COMPARISON_OPERATORS = {
    gte: 'gte',
    gt: 'gt',
    lte: 'lte',
    lt: 'lt',
    eq: 'eq',
};

export const filter = (data, filters) => {
    const filteredData = data?.filter((item) => {
        // contains the result of each filter (true or false)
        let allFilterResults = [];

        for (let i = 0; i < filters.length; i++) {
            const currFilter = filters[i];

            // true or false
            const currFilterResults = currFilter.fieldsToCheckAgainst.some(
                (fieldToCheckAgainst) => {
                    const value = get(item, fieldToCheckAgainst);
                    if (typeof currFilter.value === 'number') {
                        switch (currFilter.comparisonOperator) {
                            case COMPARISON_OPERATORS.gte:
                                return value >= currFilter.value;
                            case COMPARISON_OPERATORS.gt:
                                return value > currFilter.value;
                            case COMPARISON_OPERATORS.lte:
                                return value <= currFilter.value;
                            case COMPARISON_OPERATORS.lt:
                                return value < currFilter.value;
                            case COMPARISON_OPERATORS.eq:
                                return value === currFilter.value;
                        }
                    }

                    // Filter does not have a value, so that means that it is not active
                    // and the item can pass through
                    if (!currFilter.value) {
                        return true;
                    }

                    if (
                        typeof currFilter.value === 'string' &&
                        currFilter.comparisonOperator !==
                            COMPARISON_OPERATORS.eq
                    ) {
                        return value
                            .toLowerCase()
                            .includes(currFilter.value.toLocaleLowerCase());
                    }

                    return value === currFilter.value;
                }
            );

            allFilterResults = allFilterResults.concat(currFilterResults);
        }

        // Return only the items that satisfy all the active filters
        return allFilterResults.every((result) => result === true);
    });

    return filteredData;
};
